import { useLayoutEffect, useState } from 'react';
// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Typography, useMediaQuery } from '@mui/material';

// project-imports
import NavGroup from './NavGroup';
import menuItem from 'menu-items';

import { useSelector } from 'store';
import useConfig from 'hooks/useConfig';
import { HORIZONTAL_MAX_ITEM } from 'config';

import { usePermissions } from 'contexts/PermissionContext';
import useAnnouncements from 'contexts/AnnouncementsContext';

// types
import { NavItemType } from 'types/menu';
import { MenuOrientation } from 'types/config';

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

// Rendered *in place of* the Announcements icon while something is unread, rather than as a
// separate chip on the right — the count is the thing worth looking at, and it survives the
// collapsed drawer, where the icon slot is all that is rendered.
//
// NavItem treats `icon` as a component (`const Icon = item.icon; <Icon variant size sx />`),
// so a plain function component drops straight in; `OverrideIcon` already admits
// FunctionComponent<any>. `variant` and `sx` are accepted and ignored — NavItem passes them
// unconditionally and they are meaningful only to the iconsax icons this replaces.
//
// Built per count because NavItem gives the icon no way to receive extra props. That costs
// nothing here: filterMenuByPermission only re-runs when `unreadCount` actually changes.
const makeUnreadBadgeIcon = (count: number) => {
  const UnreadBadgeIcon = ({ size = 20 }: { size?: number; variant?: string; sx?: unknown }) => (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: '50%',
        bgcolor: 'error.main',
        color: 'error.contrastText',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // 9+ rather than a widening pill: the circle has to stay inside the icon slot.
        fontSize: count > 9 ? 9 : 11,
        fontWeight: 700,
        lineHeight: 1
      }}
    >
      {count > 9 ? '9+' : count}
    </Box>
  );

  return UnreadBadgeIcon;
};

const Navigation = () => {
  const theme = useTheme();

  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const { menuOrientation } = useConfig();
  const { drawerOpen } = useSelector((state) => state.menu);

  const { hasPermission } = usePermissions();

  // Announcements is the one item whose visibility is data-dependent — a client sees the
  // tab only once an announcement targets them — so no PERMISSIONS string can express it
  // and filterMenuByPermission needs this extra check. `unreadCount` rides along from the
  // same call and drives the badge.
  const { visible: canSeeAnnouncements, unreadCount } = useAnnouncements();

  const [selectedItems, setSelectedItems] = useState<string | undefined>('');
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  const [menuItems, setMenuItems] = useState<{ items: NavItemType[] }>({ items: [] });

  const filterMenuByPermission = (items: NavItemType[]): NavItemType[] => {
    return items
      .reduce<NavItemType[]>((acc, item) => {
        // Check item's own permission first
        const hasItemPermission = item.permission ? hasPermission(item.permission) : true;
        if (item.permission && !hasItemPermission) {
          return acc;
        }

        if (item.id === 'announcements') {
          if (!canSeeAnnouncements) {
            return acc;
          }
          // Swapped in here rather than in menu-items because the count is per user and
          // changes as they read. The badge replaces the icon instead of sitting beside
          // it — at 0 unread the item keeps its normal icon, so the slot is never empty.
          if (unreadCount > 0) {
            acc.push({ ...item, icon: makeUnreadBadgeIcon(unreadCount) });
            return acc;
          }
        }

        // Recursively filter children
        const filteredChildren = item.children ? filterMenuByPermission(item.children) : undefined;

        // Hide group if it has no accessible children after filtering
        if (item.type === 'group' && (!filteredChildren || filteredChildren.length === 0)) {
          return acc;
        }

        acc.push({ ...item, children: filteredChildren });
        return acc;
      }, []);
  };

  const ensureFirstItemUrlIsRoot = (items: NavItemType[]): NavItemType[] => {
    const setFirstUrl = (nodes: NavItemType[]): boolean => {
      for (const node of nodes) {
        if (node.type === 'item') {
          node.url = '/';
          return true;
        }
        if (node.children && setFirstUrl(node.children)) {
          return true;
        }
      }
      return false;
    };

    setFirstUrl(items);
    return items;
  };

  useLayoutEffect(() => {
    const filtered = filterMenuByPermission(menuItem.items);
    setMenuItems({ ...menuItem, items: ensureFirstItemUrlIsRoot(filtered) });
    // eslint-disable-next-line
  }, [menuItem, canSeeAnnouncements, unreadCount]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = menuItems.items.length - 1;
  let remItems: NavItemType[] = [];
  let lastItemId: string;

  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1].id!;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items.slice(lastItem - 1, menuItems.items.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon
    }));
  }
  const navGroups = menuItems.items.slice(0, lastItemIndex + 1).map((item) => {
    switch (item.type) {
      case 'group':
        return (
          <NavGroup
            key={item.id}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedItems={selectedItems}
            lastItem={lastItem!}
            remItems={remItems}
            lastItemId={lastItemId}
            item={item}
          />
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Navigation Group
          </Typography>
        );
    }
  });

  return (
    <Box
      sx={{
        pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
        '& > ul:first-of-type': { mt: 0 },
        display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block'
      }}
    >
      {navGroups}
    </Box>
  );
};

export default Navigation;
